// Interaction washes — DESIGN_LANGUAGE.md §4.3. Byte-identical to the house
// washes previously pinned as string literals by themeChrome.test.js
// (light-dark(rgba(20,20,22,0.08), rgba(255,255,255,0.1)) and the
// 0.05/0.07 hover pair). Adaptive pairs are [lightValue, darkValue]
// (scratch system/color.js normalizes arrays as { '@light': v[0], '@dark': v[1] }).
// Canonical home is brand designSystem/color.js — this copy carries the
// tokens for deployed workspace builds until the brand publish lands.
export default {
  // §4.4 accent ink — accent-hue links/actions must hold AA as SMALL-text ink
  // on BOTH stages AND raised surfaces. Brand `blue` (#0085FF) measured
  // 2.8–3.5:1 on light tints and 4.27:1/3.41:1 on dark gray3/gray4 cards
  // (~460 instances: admin/logs, tickets chips, marketplace Install, /my
  // quick links — 2026-08-08 AA walk). Both halves re-tone the same hue:
  // deepened for light (≥4.9:1 on white/gray15/gray16), brightened for dark
  // (≥5:1 on gray1–gray4). [lightValue, darkValue].
  // Light half deepened #0A66CC → #0A5CB8 (2026-08-08): the first value held
  // on white/gray16/gray15 (5.55/5.32/4.92) but only reached 4.25 on the
  // gray14-class page WASH — where a lot of chrome actually sits, so eyebrows
  // there still failed after the swap. #0A5CB8 clears every light ground:
  // 6.49 white / 6.22 gray16 / 5.75 gray15 / 4.97 wash / 4.14 gray13 (the
  // last is a card-edge tint that carries no small text).
  blueDeep: '#0A5CB8',
  blueBright: '#47A8FF',
  accentInk: ['blueDeep', 'blueBright'],

  // Semantic INK pairs — same construction as accentInk, for the cases where
  // a status hue must be the TEXT rather than a wash or a dot (§4.2 + §8.5
  // rule 3: prefer wash+neutral ink; reach for these only when the hue itself
  // is the signal, e.g. a lone "on"/"3 failed" figure with no plate). The raw
  // brand hues measured 2.15 (green), 2.45 (red), 2.73 (orange), 2.4
  // (magenta) as bold ink on the gray14-class wash — every one under the 4.5
  // floor. Each pair is verified ≥4.7 on white/gray16/gray15/wash (light) and
  // ≥5 on gray1–gray4 (dark).
  greenDeep: '#2A6E28',
  greenBright: '#7FD07B',
  successInk: ['greenDeep', 'greenBright'],
  redDeep: '#A83520',
  redBright: '#FFA191', // = brand (company dangerInkOnRaised); brand wins the merge
  dangerInk: ['redDeep', 'redBright'],
  orangeDeep: '#94500F',
  orangeBright: '#F4A45E',
  warningInk: ['orangeDeep', 'orangeBright'],
  magentaDeep: '#A100A1',
  magentaBright: '#FF7AFF',
  magentaInk: ['magentaDeep', 'magentaBright'],

  // THE TINT PLATE BASE — what a hue chip mixes its own ink into
  // (`color-mix(in srgb, currentColor N%, var(--color-tintBase))`), instead of
  // into `transparent`. A transparent tint lets the ground show through, and
  // the inks above are tuned for the page and card grounds only: on a raised or
  // grey fill (dark ~67–82, light ~181–226) a hue chip fell to 2.7–4.2:1 —
  // measured on dev 2026-10-07 (accent 2.69 on a raised card, warning 3.47,
  // danger 3.59; light accent 4.01, danger 3.15). An opaque plate built on a
  // fixed base keeps the ink's own contrast on every ground: white in light,
  // the card colour in dark (`cardSolid`'s dark half), so a chip on a card
  // looks exactly as before. 10–20 % of every ink here stays >= 4.5:1
  // (__tests__/tintBaseOwnTint.test.js).
  tintBase: ['white', 'gray3'],
  hover: ['black.05', 'white.07'],
  selected: ['gray2.08', 'white.1'],

  // THE CARD'S HOVER AND PRESS (Nika 2026-10-23: "on widgets and sections
  // colors on dark mode, make sections darker on hover instead of light"):
  // the wash over a CARD — a dashboard widget, a Fly page's section, a Fly
  // start tile, a summary tile. Light: the house hover and press, a step
  // darker. Dark: a step darker too. The house `hover` (white) lifted the dark
  // glass card (about rgb 33 on the rgb 69 stage) to about rgb 64, the stage's
  // own grey, so a hovered card lost its edge (measured on dev 2026-10-23).
  // Black 25 % takes it to about rgb 25: the same perceived step as light's
  // (about 4 L*). Controls keep `hover` / `selected`.
  cardHover: ['black.05', 'black.25'],
  cardPress: ['gray2.08', 'black.35'],

  // Workspace STAGE + widget icon plate — canonical in brand color.js (same
  // mirror rule as the washes above). `stage` is the ground under the home
  // dashboard and every canvas page (NOT the PageSlot, which stays on
  // `document`). Replaces the per-workspace settings.designSystem record that
  // skinned only one workspace (Nika 2026-08-15: make it general).
  //
  // Dark = `gray5`, NOT the document's dark half. `gray1+4` resolved to
  // rgb(8,8,8) — effectively black, which Nika rejected on sight (2026-08-15:
  // "fix background - not black - use one that codex created"). `gray5`
  // (rgb(69,69,75)) is the adaptive neutral canvas Codex shipped for /files
  // and the /tasks board (CODEX-V3), and those two surfaces still paint it
  // via `var(--canvas-bg, var(--color-gray5))` — so before this change the
  // home board and the canvas pages were near-black while Files and the task
  // board sat on a mid gray. Pointing `stage` at the same tone is what makes
  // the ground actually general instead of general-but-different-per-surface.
  stage: ['milk', 'gray5'],
  widgetPlate: ['gray2.07', 'white.13']

  // `raised` (the lifted thumb / keycap fill, ['white', 'gray5']) was staged
  // here until brand 3.14.798 promoted it byte-identical (lane D1) — brand
  // designSystem/color.js.
}
